<script setup lang="ts">
import { computed, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { storeToRefs } from 'pinia';
import { useQuasar } from 'quasar';

import ReorderableTable from '@/components/ReorderableTable.vue';

import { useEventListItemsStore } from '@/stores/eventListItems.store';
import type { ProjectModuleEventItemConfigDto, ProjectModuleDto, Locales } from '@/services/types/api.types';
import { getTranslation } from '@/composables/data-display-utils';
import router from '@/router';
import { formatDate } from '@/plugins/i18n';
import EventItemDialog from '@/components/dialogs/EventItemDialog.vue';

// CONST
const { t } = useI18n();
const $q = useQuasar();

// STORE
const eventListItemsStore = useEventListItemsStore();
const { orderedEventListItems } = storeToRefs(eventListItemsStore);

// PROPS
const props = defineProps<{
  module: ProjectModuleDto;
  languageEnabled: Locales[];
}>();

// COMPUTED
const columns = computed(() => [
  {
    name: 'title',
    label: t('global.label.title'),
    field: (row: ProjectModuleEventItemConfigDto) => getTranslation(row, 'title', props.languageEnabled),
    align: 'left',
  },
  {
    name: 'status',
    label: t('global.label.status'),
    field: (row: ProjectModuleEventItemConfigDto) => getStatus(row),
    align: 'left',
  },
  {
    name: 'date',
    label: t('global.label.date'),
    field: (row: ProjectModuleEventItemConfigDto) => {
      return row.begin_date && row.end_date
        ? `${formatDate(new Date(row.begin_date))} - ${formatDate(new Date(row.end_date))}`
        : row.begin_date
          ? formatDate(new Date(row.begin_date))
          : '';
    },
    align: 'left',
  },
  {
    name: 'time',
    label: t('global.label.time'),
    field: (row: ProjectModuleEventItemConfigDto) => {
      return row.start_time !== '00:00:00' && row.end_time !== '00:00:00'
        ? `${row.start_time} - ${row.end_time}`
        : row.start_time !== '00:00:00'
          ? row.start_time
          : '';
    },
    align: 'left',
  },
]);

// LIFECYCLE
onMounted(async () => {
  if (props.module.id) {
    await eventListItemsStore.fetchEventListItemsByModuleId(props.module.id);
  }
});

// FUNCTIONS
function getStatus(eventItem: ProjectModuleEventItemConfigDto) {
  const status = eventListItemsStore.getStatuses();
  return status.find((s) => s.value === eventItem.status).label;
}

const handleAddRow = () => {
  if (props.module.id) {
    const eventItemConfig: ProjectModuleEventItemConfigDto = eventListItemsStore.createEventListItem(props.module.id);
    $q.dialog({
      component: EventItemDialog,
      componentProps: {
        mode: 'add',
        eventItemConfig,
        languageEnabled: props.languageEnabled,
        module: props.module,
      },
    });
  }
};

const handleDeleteRow = (row: ProjectModuleEventItemConfigDto) => {
  $q.dialog({
    title: t('global.dialogs.deleteTitle', { type: t('project-module.event.title') }),
    message: t('global.dialogs.deleteMessage', { type: t('project-module.event.title') }),
    cancel: true,
    persistent: true,
  }).onOk(async () => {
    await eventListItemsStore.deleteEventListItem(row);
  });
};

const handleRowClick = (row: ProjectModuleEventItemConfigDto) => {
  router.push({
    name: 'event_item_details',
    params: { id: row.id, projectId: props.module.project_id, moduleId: props.module.id },
  });
};
</script>
<template>
  <div class="event-list">
    <reorderable-table
      :rows="orderedEventListItems"
      :columns="columns"
      :title="t('project-module.event.title')"
      itemKey="id"
      @row-click="handleRowClick"
      @add-row="handleAddRow"
      @delete-row="handleDeleteRow"
      :disableOrderable="true"
    />
  </div>
</template>
